import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section id="categories" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-16 md:px-6 md:py-20">
      <div className="text-center">
        <h2 className="font-heading text-2xl font-semibold text-foreground-950 md:text-4xl">
          {t('home.categoriesTitle')}
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
          {t('home.categoriesSubtitle')}
        </p>
      </div>

      <div className="mt-10 flex flex-col items-center gap-10">
        {/* Dropdown */}
        <div className="w-full max-w-md" ref={ref}>
          <label className="mb-2 block text-sm font-medium text-foreground-700">
            {t('home.dropdownLabel')}
          </label>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-background-300 bg-background-50 px-4 py-3.5 text-left text-sm text-foreground-800 transition-colors hover:border-primary-300 cursor-pointer"
          >
            <span className="flex items-center gap-2 truncate">
              {selectedKey ? (
                <>
                  <i className={`${categories.find((c) => c.key === selectedKey)?.icon ?? 'ri-gift-line'} text-foreground-500`}></i>
                  <span>{t('categories.' + selectedKey)}</span>
                </>
              ) : (
                <span className="text-foreground-500">{t('home.dropdownPlaceholder')}</span>
              )}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${open ? 'rotate-180' : ''}`}></i>
          </button>

          {open && (
            <div className="absolute z-30 mt-2 max-h-80 w-full max-w-md overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-1">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setSelectedKey(cat.key)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                    <i className={cat.icon}></i>
                  </span>
                  <span className="truncate">{t('categories.' + cat.key)}</span>
                  <i className="ri-arrow-right-up-line ml-auto text-foreground-400"></i>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Category grid */}
        <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {categories.map((cat) => (
            <a
              key={cat.key}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={cat.image}
                  alt={`${t('categories.' + cat.key)} — AllChinaBuy Spreadsheet category`}
                  title={`${t('categories.' + cat.key)} AllChinaBuy Spreadsheet`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-md bg-background-50/90 text-primary-700">
                  <i className={cat.icon}></i>
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 p-4">
                <span className="font-heading text-sm font-semibold text-foreground-900">
                  {t('categories.' + cat.key)}
                </span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-500 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}